import { useMemo } from 'react';
import { ChevronDown, FlaskConical, Loader2 } from 'lucide-react';
import { ROLES, type Role } from '@nexops/shared';
import { useT } from '@/i18n/I18nProvider';
import { useCurrentUser } from '@/features/auth/auth.store';
import type { DemoUser } from '@/features/auth/types';
import { useDemoSwitch, useDemoUsers } from '@/features/auth/useSession';
import { cn } from '@/shared/lib/cn';
import {
  Avatar,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  type BadgeTone,
} from '@/shared/ui';

export const ROLE_TONE: Record<Role, BadgeTone> = {
  ADMIN: 'danger',
  BOARD: 'violet',
  SALES_MANAGER: 'info',
  SALES_REP: 'success',
  LOGISTICS: 'warning',
  ORDER_DESK: 'primary',
  COLLECTIONS: 'neutral',
  DRIVER: 'success',
  FLEET_ADMIN: 'warning',
  DISPATCHER: 'info',
  WAREHOUSE_MANAGER: 'warning',
  WAREHOUSE: 'neutral',
};

/**
 * "Demo mode" role switcher — very visible on purpose: the presentation shows how data scope
 * changes per role. Hidden entirely when `/demo/users` returns 404 (demo mode off).
 */
export function DemoSwitcher() {
  const t = useT();
  const me = useCurrentUser();
  const { data: users, error, isLoading } = useDemoUsers();
  const switchUser = useDemoSwitch();

  const groups = useMemo(() => {
    const byRole = new Map<Role, DemoUser[]>();
    for (const u of users ?? []) byRole.set(u.role, [...(byRole.get(u.role) ?? []), u]);
    return ROLES.filter((r) => byRole.has(r)).map((role) => ({ role, users: byRole.get(role)! }));
  }, [users]);

  // 404/403 → demo mode disabled; any other error → also hide (never block the real UI).
  if (isLoading || error || !users?.length || !me) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={cn(
          'flex h-8 shrink-0 items-center gap-2 whitespace-nowrap rounded-md border border-dashed border-warning/60 bg-warning-soft pl-2 pr-1.5 text-xs font-medium text-warning transition-colors hover:border-warning focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-warning/40',
        )}
      >
        {switchUser.isPending ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <FlaskConical className="size-3.5" />
        )}
        <span className="hidden font-semibold uppercase tracking-wide sm:inline">{t('demo.mode')}</span>
        <span className="hidden h-4 w-px bg-warning/30 2xl:block" aria-hidden />
        <span className="hidden max-w-[9rem] truncate text-foreground 2xl:inline">
          {t(`roles.${me.role}`)}
        </span>
        <ChevronDown className="size-3.5 opacity-70" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="max-h-[70vh] w-72 overflow-y-auto">
        <div className="px-2 pb-2 pt-1.5">
          <p className="text-xs font-semibold text-foreground">{t('demo.switchAs')}</p>
          <p className="mt-0.5 text-2xs text-muted-foreground">{t('demo.hint')}</p>
        </div>
        {groups.map(({ role, users: list }) => (
          <DropdownMenuGroup key={role}>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="flex items-center gap-1.5">
              <Badge tone={ROLE_TONE[role]} dot>
                {t(`roles.${role}`)}
              </Badge>
            </DropdownMenuLabel>
            {list.map((u) => {
              const current = u.id === me.id;
              return (
                <DropdownMenuItem
                  key={u.id}
                  disabled={current || switchUser.isPending}
                  onSelect={() => switchUser.mutate(u.id)}
                  className="gap-2.5"
                >
                  <Avatar firstName={u.firstName} lastName={u.lastName} />
                  <span className="min-w-0 flex-1 truncate text-[13px] text-foreground">
                    {u.firstName} {u.lastName}
                  </span>
                  {current && <Badge tone="primary">{t('demo.current')}</Badge>}
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuGroup>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
